/**
 * Small lexical helpers the sources share. They read the text BEFORE the caret
 * only and are approximate on purpose (no parser): a wrong guess costs a missing
 * or extra suggestion, never an edit.
 */

/** How far back the scanners look — bounds the work per keystroke. */
const LOOKBACK = 4000

export interface LineScan {
  /** The caret sits in a `//` / `#` / `--` line comment. */
  comment: boolean
  /** Offset (in `doc`) of the opening quote when the caret sits inside a string on this line. */
  stringStart: number | null
}

/**
 * Scan the caret's line up to the caret. `lineComment` is the language's line
 * comment marker. Strings are single-line ('…', "…", and `…` treated as one line).
 */
export function scanLine(doc: string, pos: number, lineComment: string): LineScan {
  const lineStart = doc.lastIndexOf('\n', pos - 1) + 1
  let quote: string | null = null
  let quoteAt = -1
  for (let i = lineStart; i < pos; i++) {
    const ch = doc[i]!
    if (quote) {
      if (ch === '\\') { i++; continue }
      if (ch === quote) quote = null
      continue
    }
    if (doc.startsWith(lineComment, i)) return { comment: true, stringStart: null }
    if (ch === '"' || ch === "'" || ch === '`') { quote = ch; quoteAt = i }
  }
  return { comment: false, stringStart: quote ? quoteAt : null }
}

/** The identifier-ish word ending at the caret (`[A-Za-z0-9_$]`). */
export function wordBefore(doc: string, pos: number, re = /[\w$]/): string {
  let i = pos
  while (i > 0 && re.test(doc[i - 1]!)) i--
  return doc.slice(i, pos)
}

export interface Opener { ch: '(' | '[' | '{'; at: number }

/**
 * The unmatched `(`, `[`, `{` before the caret, outermost first, skipping strings
 * and line comments. `blockComment` (`/*`) is skipped for JS and SQL.
 */
export function openersBefore(doc: string, pos: number, lineComment: string, blockComment = true): Opener[] {
  const start = Math.max(0, pos - LOOKBACK)
  const stack: Opener[] = []
  let quote: string | null = null
  for (let i = start; i < pos; i++) {
    const ch = doc[i]!
    if (quote) {
      if (ch === '\\') { i++; continue }
      if (ch === quote || (ch === '\n' && quote !== '`' && quote !== '"""')) quote = null
      continue
    }
    if (doc.startsWith(lineComment, i)) {
      const nl = doc.indexOf('\n', i)
      if (nl < 0 || nl >= pos) break
      i = nl
      continue
    }
    if (blockComment && doc.startsWith('/*', i)) {
      const end = doc.indexOf('*/', i + 2)
      if (end < 0 || end >= pos) break
      i = end + 1
      continue
    }
    if (ch === '"' || ch === "'" || ch === '`') { quote = ch; continue }
    if (ch === '(' || ch === '[' || ch === '{') stack.push({ ch, at: i })
    else if (ch === ')' || ch === ']' || ch === '}') stack.pop()
  }
  return stack
}

/** The dotted name right before offset `end` (`papan.db.run` before a `(`), or ''. */
export function calleeBefore(doc: string, end: number): string {
  const m = doc.slice(Math.max(0, end - 300), end).match(/([A-Za-z_$][\w$]*(?:\s*\??\.\s*[A-Za-z_$][\w$]*)*)\s*$/)
  return m ? m[1]!.replace(/\s*\??\.\s*/g, '.') : ''
}

/** Count the commas at depth 0 in `text` (the arguments already written before the caret). */
export function topLevelCommas(text: string): number {
  let depth = 0
  let quote: string | null = null
  let n = 0
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!
    if (quote) {
      if (ch === '\\') { i++; continue }
      if (ch === quote) quote = null
      continue
    }
    if (ch === '"' || ch === "'" || ch === '`') quote = ch
    else if (ch === '(' || ch === '[' || ch === '{') depth++
    else if (ch === ')' || ch === ']' || ch === '}') depth--
    else if (ch === ',' && depth === 0) n++
  }
  return n
}

/** The first argument of a call when it is a plain string literal: `('_db/x.sql', …` → `_db/x.sql`. */
export function firstStringArg(argsText: string): string | null {
  const m = argsText.match(/^\s*(['"`])([^'"`\n]*)\1/)
  return m ? m[2]! : null
}

/** Keys already written in an object literal's text (`{ a: 1, "b": 2` → a, b). */
export function writtenKeys(objectText: string): Set<string> {
  const keys = new Set<string>()
  for (const m of objectText.matchAll(/(?:^|[,{])\s*(['"]?)([\w$]+)\1\s*:/g)) keys.add(m[2]!)
  return keys
}

/** The word containing offset `pos` (the hovered character), or null when `pos` isn't on one. */
export function wordAt(doc: string, pos: number, re = /[\w$]/): { from: number; to: number; word: string } | null {
  if (pos < 0 || pos >= doc.length || !re.test(doc[pos]!)) return null
  let from = pos
  let to = pos + 1
  while (from > 0 && re.test(doc[from - 1]!)) from--
  while (to < doc.length && re.test(doc[to]!)) to++
  return { from, to, word: doc.slice(from, to) }
}

/** The single-line string literal containing offset `pos` (quotes excluded), or null. */
export function stringAt(doc: string, pos: number, lineComment: string): { from: number; to: number; text: string } | null {
  const line = scanLine(doc, pos + 1, lineComment)
  if (line.comment || line.stringStart === null) return null
  const quote = doc[line.stringStart]!
  const from = line.stringStart + 1
  let to = from
  while (to < doc.length && doc[to] !== quote && doc[to] !== '\n') to += doc[to] === '\\' ? 2 : 1
  if (pos < from || pos >= to) return null
  return { from, to, text: doc.slice(from, to) }
}
